import * as React from 'react'
import * as cn from 'classnames'
import * as styles from './Hover.module.scss'
import { WatchlistAction, DVString, TrailerFragment, WPSClient, withArgs, Args, ItemMaturityRating, StorefrontItem, EnrichmentRequest, StorefrontItemLive } from 'DVWebNodeUtils'
import WatchlistStatus from './WatchlistStatus'
import { InfoboxProps, Infobox } from './Infobox'
import { Icon, IconType, Badge, badges, Hover as BasicHover } from 'DVWebNodeUI'
import { CommonPlaybackAction } from './Headline/PlaybackBlock'
import { ReviewsRating } from './ReviewsRating'
import { Tooltip } from './Tooltip'
import { Headline } from './Headline'
import { CustomerReviews } from 'util/types/wps/hover'

export const stringifyEnrichmentsOnClient = (enrichment?: EnrichmentRequest[] | undefined): EnrichmentRequest[] | string => (
    global.__SERVER__ === true ? enrichment : enrichment && JSON.stringify(enrichment)
)

interface UpdateableHoverProps {
    title?: string
    titleID?: string
    titleIDType?: "EPISODE" | "SEASON" | "MOVIE" | "CHANNEL" | "LIVEEVENT" | "UNKNOWN"
    synopsis?: string
    hasSubtitles?: boolean
    watchlistAction?: WatchlistAction
    liveInfo?: StorefrontItemLive
    liveBadgeLabel?: string
    playbackAction?: CommonPlaybackAction
    maturityRating?: ItemMaturityRating
    imdbRating?: string
    releaseYear?: string | number
    runtime?: string
    trailer?: TrailerFragment
    offers?: DVString[]
    infoboxes?: InfoboxProps[]
    customerReviews?: CustomerReviews
    errored?: boolean
}

interface HoverProps extends UpdateableHoverProps {
    className?: string
    children: React.ReactChild | React.ReactChild[]
    placeholderClassName?: string
    enrichTrailer?: boolean
    onMouseOver?: (event: React.MouseEvent<HTMLDivElement>) => void
    onMouseLeave?: (event: React.MouseEvent<HTMLDivElement>) => void
    onWatchlistToggle?: (newAction: WatchlistAction) => void
}

declare let window: Window & {
    P: any
}

export interface PlayerCloseOpt {
    //This is incomplete, and declares only the parts needed for our current use case.
    videoType: string
    asin: string
    trailer: boolean
}

export interface RegulatoryRatingBadgeProps {
    displayText: string;
    description: string;
    countryCode?: string;
    simplifiedId?: string;
}


const TrailerWrapper = ({ trailer, children }: { trailer: TrailerFragment, children: any }) => (
    <Tooltip text={trailer.text}>
        <a
            className={'js-deeplinkable'}
            href={trailer.playbackURL}
            data-page-title-id={trailer.playbackID}
            data-video-type={trailer.videoMaterialType}
            data-use-full-view="true"
            data-is-trailer="true"
            data-title-id={trailer.playbackID}
            data-ux-click="true">
            {children}
        </a>
    </Tooltip>
)

const IconWrapper = ({ children }: { children: any }) => (
    <div className={cn(styles.iconWrapper, styles.alignBottom, styles.spaceRight)}>
        {children}
    </div>
)

const createPlaybackAction = (enrichment: StorefrontItem): CommonPlaybackAction => {
    if (!enrichment)
        return null

    if (enrichment && !enrichment.playbackAction)
        return null

    const newProgress = enrichment.progress && enrichment.progress.percentage
    const newPlaybackAction = enrichment.playbackAction

    const merged = {
        ...newPlaybackAction,
        progress: newProgress
    }

    return merged
}

const extractTitleIDTypeFromWatchlistAction = (watchlistAction: WatchlistAction) => {
    const valueInWatchlist = watchlistAction && watchlistAction.endpoint &&
        watchlistAction.endpoint.query && watchlistAction.endpoint.query.titleType
    if (valueInWatchlist) {
        const asLower = valueInWatchlist.toLowerCase()
        if (asLower == "season")
            return "SEASON"
        if (asLower == "event")
            return "LIVEEVENT"
    }
    return "UNKNOWN"
}

const extractTitleIdFromWatchlistAction = (watchlistAction: WatchlistAction) => {
    return watchlistAction &&
        watchlistAction.endpoint &&
        watchlistAction.endpoint.query &&
        watchlistAction.endpoint.query.titleID
}


class HoverComponent extends React.Component<HoverProps & Args, UpdateableHoverProps> {
    enrichmentInProgress: boolean
    initialEnrichmentComplete: boolean
    canEnrichTrailer: boolean

    constructor(props: HoverProps & Args) {
        super(props)
        this.state = {
            ...this.props,
            titleIDType: this.props.titleIDType || extractTitleIDTypeFromWatchlistAction(this.props.watchlistAction),
            titleID: this.props.titleID || extractTitleIdFromWatchlistAction(this.props.watchlistAction)
        }
        this.canEnrichTrailer = !Boolean(this.props.trailer)
    }

    isThisTitle(comparisonID: string) {
        const isScheduledId = this.state.titleID && this.state.titleID == comparisonID
        const isPlaybackActionId = this.state.playbackAction &&
            this.state.playbackAction.titleID &&
            this.state.playbackAction.titleID == comparisonID
        return isScheduledId || isPlaybackActionId
    }

    enrichMe(fetchTrailer: boolean, fetchPlayback: boolean, setEnrichedOnce?: boolean) {
        if (this.enrichmentInProgress)
            return
        const shouldCallCcts = this.state.titleIDType &&
            (this.state.titleIDType == "SEASON" || this.state.titleIDType == "LIVEEVENT")
        this.enrichmentInProgress = true
        const titleID = this.state.titleID
        const isLive = Boolean(this.state.liveBadgeLabel || this.state.liveInfo)

        if (titleID && this.state.titleIDType != "CHANNEL") {
            const enrichmentRequest = {
                titleID: titleID,
                titlePlayable: !shouldCallCcts,
                trailer: fetchTrailer,
                playback: fetchPlayback,
                ...(isLive && { live: isLive })
            }

            const wps = new WPSClient()
            wps.EnrichItemMetadata({
                context: this.props.context,
                itemsToEnrich: stringifyEnrichmentsOnClient([enrichmentRequest])
            }).then(response => {
                if (response.enrichments && response.enrichments[titleID]) {

                    const enrichment = response.enrichments[titleID]

                    this.setState(
                        {
                            trailer: enrichment.trailer || this.state.trailer,
                            playbackAction: fetchPlayback ? createPlaybackAction(enrichment) : this.state.playbackAction
                        }
                    )
                }
                if (setEnrichedOnce)
                    this.initialEnrichmentComplete = true
                if (fetchTrailer && this.canEnrichTrailer)
                    this.canEnrichTrailer = false
                this.enrichmentInProgress = false
            }
            ).catch(error => {
                this.props.logger.error(error)
                this.enrichmentInProgress = false
            })
        }
    }

    onHover(ev: React.MouseEvent<HTMLDivElement>) {
        if (this.props.onMouseOver)
            this.props.onMouseOver(ev)

        if (!this.initialEnrichmentComplete)
            this.enrichMe(this.canEnrichTrailer, true, true)
    }

    componentDidMount() {
        if (window && window.P) {
            window.P.when('dv-player-integration').execute((player: any) => {
                player.on('play', (opt: PlayerCloseOpt) => {
                    const playingTitle = opt.asin
                    const isPlayingCurrentItem = this.isThisTitle(playingTitle)
                    if (isPlayingCurrentItem) {
                        const fetchTrailer = true
                        const fetchPlayback = true
                        player.on('closed', () => this.enrichMe(fetchTrailer, fetchPlayback)
                        )
                    }
                })
            }
            )
        }
    }

    componentDidCatch() {
        this.setState({ errored: true })
    }

    render() {
        if (this.state.errored)
            return this.props.children
        const watchlistAction = this.state.watchlistAction &&
            { ...this.state.watchlistAction, text: this.state.watchlistAction.text as DVString }

        const preFirstLine = (this.state.liveBadgeLabel || (this.state.liveInfo &&
            this.state.liveInfo.status == "LIVE" &&
            this.state.liveInfo.label)) &&
            <div className={styles.preFirstLine}>
                <Badge className={styles.tailContent}
                    use={badges.LIVE}>
                    {this.state.liveBadgeLabel || this.state.liveInfo.label}
                </Badge>
            </div>

        const mainactions = <>
            {preFirstLine}
            <div className={styles.firstLine}>
                <Headline playbackAction={this.state.playbackAction}
                    offers={this.state.offers}
                    title={this.state.title} />
                <div className={styles.additionalIcons}>
                    {this.state.trailer &&
                        <IconWrapper>
                            <TrailerWrapper
                                trailer={this.state.trailer} >
                                <Icon use={IconType.TRAILER} className={cn(styles.tailContent, styles.alignBottom)} />
                            </TrailerWrapper>
                        </IconWrapper>}
                    {watchlistAction &&
                        <IconWrapper>
                            <WatchlistStatus
                                onToggle={this.props.onWatchlistToggle}
                                watchlistAction={watchlistAction}
                            />
                        </IconWrapper>}
                </div>
            </div>
        </>


        const boxes = this.state.infoboxes &&
            this.state.infoboxes.map(infobox => <Infobox {...infobox} />)

        const synopsis = <div className={styles.summary}>
            <h1 className={styles.title}>
                {this.props.title}
            </h1>
            <p className={styles.synopsis}>
                {this.state.synopsis}
            </p>
        </div>

        const extraMetadata = <>
            {this.state.liveInfo && this.state.liveInfo.timeBadge &&
                <div className={styles.extraMetadata}            >
                    {this.state.liveInfo && this.state.liveInfo.timeBadge &&
                        <span className={cn(styles.tailContent, styles.tailText)}>
                            {this.state.liveInfo.timeBadge}
                        </span>}
                </div>}
            <div className={styles.extraMetadata}>
                {this.state.liveInfo && this.state.liveInfo.venue &&
                    <div className={cn(styles.tailContent, styles.tailText)}>
                        {this.state.liveInfo.venue}
                    </div>}
                {this.state.customerReviews &&
                    <div className={styles.tailContent}>
                        <ReviewsRating rating={this.state.customerReviews.value} />
                    </div>}
                {this.state.imdbRating &&
                    <div className={cn(styles.tailContent, styles.tailText)}>
                        IMDb {this.state.imdbRating}
                    </div>}
                {this.state.runtime &&
                    <div className={cn(styles.tailContent, styles.tailText)}>
                        {this.state.runtime}
                    </div>}
                {this.state.releaseYear &&
                    <div className={cn(styles.tailContent, styles.tailText)}>
                        {this.state.releaseYear}
                    </div>}
                {this.state.hasSubtitles &&
                    <div className={cn(styles.tailContent, styles.alignMiddle)} >
                        <Icon use={IconType.SUBTITLES} />
                    </div>}
                {this.state.maturityRating &&
                    <div className={cn(styles.tailContent)}>
                        <Badge use={badges.REGULATORY_RATING}>
                            {this.state.maturityRating.rating}
                        </Badge>
                    </div>}
            </div>
        </>

        const blocks = [mainactions, boxes, synopsis, extraMetadata].filter(line => Boolean(line))

        return (
            <BasicHover
                contentBlocks={blocks}
                className={this.props.className}
                placeholderClassName={this.props.placeholderClassName}
                onMouseLeave={this.props.onMouseLeave}
                onMouseOver={(ev) => this.onHover(ev)}>
                {this.props.children}
            </BasicHover>
        )
    }
}

export const Hover = withArgs(HoverComponent) as React.ComponentType<HoverProps>